import { useQuery } from '@tanstack/react-query'
import { api } from '../../../lib/api'
import { Card } from '../../ui/Card'

/**
 * Drumul mărfii, pe produs: cerut → aprobat → transferat → dat → rămas.
 *
 * Întrebarea de după eveniment — „cât am dus și cât a rămas" — cerea până acum trei ecrane și o
 * foaie de hârtie. Cifrele vin din documentele care există deja; raportul nu scrie nimic.
 *
 * `Rămas` e o deducție (transferat − predat − consum direct), nu stocul citit din ExpertAccounts.
 * Când marfa se întoarce cu un transfer de retur, acela scade din „transferat" și cifra se așază.
 */

interface Cat {
  buc: number
  lei: number
}

interface Rezumat {
  predat: Cat
  iesit: Cat
  ramasLaAgent: Cat
  consumDirect: Cat
}

interface LinieProdus {
  sku: string
  denumire: string
  cantitate: number
}

interface PeActiune {
  actiuneId: number
  denumire: string
  predat: Cat
  produse: LinieProdus[]
}

interface PeAgent {
  userId: number | null
  nume: string
  locId: number
  randuri: { sku: string; denumire: string; transferat: number; predat: number; ramas: number }[]
}

interface Rand {
  sku: string
  denumire: string
  cerut: number
  aprobat: number
  transferat: number
  predat: number
  consumDirect: number
  ramas: number
}

const lei = (n: number) =>
  n.toLocaleString('ro-RO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

/** Un rând din rezumat: eticheta, bucățile și valoarea. */
function RandRezumat({
  eticheta,
  cat,
  tare,
  indentat,
}: {
  eticheta: string
  cat: Cat
  tare?: boolean
  indentat?: boolean
}) {
  return (
    <div
      className={`flex items-center gap-3 border-b border-b4l-border px-3 py-2 text-sm last:border-0 ${
        indentat ? 'pl-7 text-b4l-text-muted' : ''
      }`}
    >
      <span className={`flex-1 ${tare ? 'font-medium' : ''}`}>{eticheta}</span>
      <span className="w-20 text-right tabular-nums">{cat.buc} buc</span>
      <span className={`w-28 text-right tabular-nums ${tare ? 'font-medium' : ''}`}>
        {lei(cat.lei)}
      </span>
    </div>
  )
}

export function FilaRaport({ campanieId }: { campanieId: number }) {
  const q = useQuery<{
    randuri: Rand[]
    rezumat: Rezumat
    peActiune: PeActiune[]
    peAgent: PeAgent[]
  }>({
    queryKey: ['campanie', campanieId, 'raport-marfa'],
    queryFn: async () => (await api.get(`/campanii/${campanieId}/raport-marfa`)).data,
  })
  const randuri = q.data?.randuri ?? []
  const rezumat = q.data?.rezumat
  const peActiune = q.data?.peActiune ?? []
  const peAgent = q.data?.peAgent ?? []
  const total = (c: keyof Rand) =>
    randuri.reduce((s, r) => s + (typeof r[c] === 'number' ? (r[c] as number) : 0), 0)

  return (
    <Card title="Raport marfă">
      {randuri.length === 0 ? (
        <p className="text-sm text-b4l-text-muted">
          Nimic de raportat încă. Rândurile apar pe măsură ce se cere marfă, se transferă și se dă
          pe teren.
        </p>
      ) : (
        <>
          {/*
            Rezumatul, cu diferența NUMITĂ. Alexandru, 22.09.2026: „amândouă, pe rânduri separate".

            Cele două cifre nu sunt egale, iar distanța dintre ele e o informație în sine — marfă
            plecată din depozit care n-a ajuns încă la nimeni. Un raport cu una singură ar lăsa
            cealaltă întrebare fără răspuns.
          */}
          {rezumat && (
            <div className="mb-4 rounded-lg border border-b4l-border">
              <RandRezumat eticheta="Predat efectiv" cat={rezumat.predat} tare />
              <RandRezumat eticheta="Ieșit din gestiune" cat={rezumat.iesit} tare />
              <RandRezumat eticheta="din care rămas la agent" cat={rezumat.ramasLaAgent} indentat />
              <RandRezumat eticheta="din care consum direct" cat={rezumat.consumDirect} indentat />
            </div>
          )}

          {/*
            Pe acțiune se arată doar ce s-a predat acolo: predările știu singure de care țin.

            Și CE anume, nu doar cât. Alexandru, 29.09.2026: „vreau să văd cantitățile pe fiecare
            acțiune". La o acțiune cu două produse date câte unul fiecărui om, „46 buc" nu spune
            nimic util.
          */}
          {peActiune.length > 0 && (
            <div className="mb-4">
              <p className="mb-1 text-xs uppercase tracking-wide text-b4l-text-dim">
                Predat, pe acțiune
              </p>
              <div className="rounded-lg border border-b4l-border">
                {peActiune.map((a) => (
                  <div key={a.actiuneId}>
                    <RandRezumat eticheta={a.denumire} cat={a.predat} />
                    {a.produse.length > 0 && (
                      <ul className="px-3 pb-2 pl-8 text-xs text-b4l-text-muted">
                        {a.produse.map((p) => (
                          <li key={p.sku} className="flex justify-between py-0.5">
                            <span>
                              {p.denumire} <span className="text-b4l-text-dim">{p.sku}</span>
                            </span>
                            <span className="tabular-nums">{p.cantitate} buc</span>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/*
            Ce poartă fiecare agent. Nu se poate pune pe acțiune: marfa nu se transferă PENTRU o
            acțiune, ci UNUI OM, în gestiunea lui. Același agent duce aceleași cutii prin cinci
            farmacii; ele nu se împart pe acțiuni.
          */}
          {peAgent.length > 0 && (
            <div className="mb-4">
              <p className="mb-1 text-xs uppercase tracking-wide text-b4l-text-dim">
                Pe agent: primit, dat, rămas
              </p>
              <div className="space-y-2">
                {peAgent.map((g) => (
                  <div key={g.locId} className="rounded-lg border border-b4l-border">
                    <div className="border-b border-b4l-border px-3 py-2 text-sm font-medium">
                      {g.nume}{' '}
                      <span className="text-xs font-normal text-b4l-text-dim">
                        gestiunea {g.locId}
                      </span>
                    </div>
                    <table className="w-full text-sm">
                      <thead className="text-left text-xs uppercase tracking-wide text-b4l-text-dim">
                        <tr>
                          <th className="px-3 py-1">Produs</th>
                          <th className="px-3 text-right">Primit</th>
                          <th className="px-3 text-right">Dat</th>
                          <th className="px-3 text-right">Rămas</th>
                        </tr>
                      </thead>
                      <tbody>
                        {g.randuri.map((r) => (
                          <tr key={r.sku} className="border-t border-b4l-border">
                            <td className="px-3 py-1">
                              {r.denumire}{' '}
                              <span className="text-xs text-b4l-text-dim">{r.sku}</span>
                            </td>
                            <td className="px-3 py-1 text-right tabular-nums">{r.transferat}</td>
                            <td className="px-3 py-1 text-right tabular-nums">{r.predat}</td>
                            <td className="px-3 py-1 text-right font-medium tabular-nums">
                              {r.ramas}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
              {/*
                Doua avertismente care trebuie sa stea LANGA cifre, nu intr-un manual: altfel
                cineva compara cu EA si crede ca unul dintre ele minte.
              */}
              <p className="mt-1 text-xs text-b4l-text-muted">
                „Rămas” e o deducție din documentele noastre — primit minus dat —, nu stocul citit
                din ExpertAccounts. Marfa dată fără proces-verbal nu se scade, deci cifra arată mai
                mult decât are agentul. Și e rămas din ce a primit <em>pentru activitatea asta</em>,
                nu stocul lui total.
              </p>
              {peAgent.some((g) => g.randuri.some((r) => r.ramas < 0)) && (
                <p className="mt-1 text-xs text-b4l-red-dark">
                  Un <strong>rămas negativ</strong> înseamnă că a predat mai mult decât figurează că
                  a primit — marfa a ajuns la el pe altă cale. Cel mai des: un transfer între
                  gestiuni făcut direct în ExpertAccounts, pe care CRM-ul nu îl vede. Când muți
                  marfă de la un agent la altul, fă transferul <em>din CRM</em>, cu sursa gestiunea
                  primului.
                </p>
              )}
            </div>
          )}

          <table className="w-full text-sm">
            <thead className="text-left text-xs uppercase tracking-wide text-b4l-text-dim">
              <tr>
                <th className="py-2">Produs</th>
                <th className="text-right">Cerut</th>
                <th className="text-right">Aprobat</th>
                <th className="text-right">Transferat</th>
                <th className="text-right">Predat</th>
                <th className="text-right">Consum direct</th>
                <th className="text-right">Rămas</th>
              </tr>
            </thead>
            <tbody>
              {randuri.map((r) => (
                <tr key={r.sku} className="border-t border-b4l-border">
                  <td className="py-2">
                    {r.denumire} <span className="text-xs text-b4l-text-dim">{r.sku}</span>
                  </td>
                  <td className="text-right tabular-nums">{r.cerut || '—'}</td>
                  <td className="text-right tabular-nums">{r.aprobat || '—'}</td>
                  <td className="text-right tabular-nums">{r.transferat || '—'}</td>
                  <td className="text-right tabular-nums">{r.predat || '—'}</td>
                  <td className="text-right tabular-nums">{r.consumDirect || '—'}</td>
                  <td
                    className={`text-right tabular-nums ${r.ramas < 0 ? 'text-b4l-red-dark' : ''}`}
                  >
                    {r.ramas}
                  </td>
                </tr>
              ))}
              <tr className="border-t-2 border-b4l-border font-medium">
                <td className="py-2">Total</td>
                <td className="text-right tabular-nums">{total('cerut')}</td>
                <td className="text-right tabular-nums">{total('aprobat')}</td>
                <td className="text-right tabular-nums">{total('transferat')}</td>
                <td className="text-right tabular-nums">{total('predat')}</td>
                <td className="text-right tabular-nums">{total('consumDirect')}</td>
                <td className="text-right tabular-nums">{total('ramas')}</td>
              </tr>
            </tbody>
          </table>
          {randuri.some((r) => r.ramas < 0) && (
            <p className="mt-3 text-xs text-b4l-red-dark">
              S-a dat mai mult decât s-a transferat pentru activitate. Fie marfa a venit din altă
              parte — un transfer nelegat de activitate, care se leagă din fila Transferuri — fie o
              predare s-a înregistrat pe acțiunea greșită.
            </p>
          )}
        </>
      )}
    </Card>
  )
}
